/* Staged integration. Activate only after isolated backend acceptance. */
(() => {
 'use strict';
 if(window.HadayaFavorites)return;
 const config=window.HadayaFavoritesConfig||{},proxyBase=config.proxyBase||'/apps/hadaya-favorites',cookieName='__Host-hadaya_favorites_guest_v1';
 let generation=0,controller,records=[],revision=0,actionToken='',actionExpiresAt=0,context='',account=false,status='loading',tail=Promise.resolve(),guestToken='',warnings=[];
 const pending=new Set();
 let analytics={active:false},analyticsState='off',analyticsRequest=0;
 if(!/^\/(apps|a|tools|community)\/[a-z0-9][a-z0-9-]*$/.test(proxyBase))throw new Error('invalid_favorites_proxy');
 const preferences=()=>window.Shopify?.customerPrivacy?.preferencesProcessingAllowed?.()===true;
 const analyticsAllowed=()=>preferences()&&window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.()===true;
 const cookie=()=>document.cookie.split('; ').find(x=>x.startsWith(cookieName+'='))?.slice(cookieName.length+1)||'';
 const clearGuest=(expected=guestToken)=>{if(cookie()===expected)document.cookie=`${cookieName}=; Max-Age=0; Path=/; Secure; SameSite=Lax`;guestToken='';};
 const notify=()=>document.dispatchEvent(new CustomEvent('hadaya:favorites',{detail:{records:structuredClone(records),status,account,warnings:[...warnings],pending:[...pending]}}));
 const storage=(key,value)=>{if(value===undefined)return localStorage.getItem(key);localStorage.setItem(key,value);};
 const warn=code=>{if(!warnings.includes(code))warnings.push(code);notify();};
 function assertCurrent(g){if(g!==generation)throw new Error('context_changed');}
 async function request(route,body,g=generation){
   const nonce=crypto.randomUUID().replaceAll('-','');
   const response=await fetch(`${proxyBase}/api/favorites/${route}?favorites_route=${route}&favorites_nonce=${nonce}`,{method:'POST',credentials:'same-origin',headers:{'Content-Type':'application/json'},body:JSON.stringify(body),signal:controller.signal,cache:'no-store'});
   assertCurrent(g);
   let data;try{data=await response.json();}catch{throw new Error('favorites_unavailable');}
   if(!response.ok)throw Object.assign(new Error(data.error||'favorites_unavailable'),{httpStatus:response.status});
   return data;
 }
 async function expectedContext(data){
   const owner=config.customerId?`customer:${config.customerId}`:null;
   if(!!owner!==data.account)return false;
   if(!owner)return true;
   const hash=await crypto.subtle.digest('SHA-256',new TextEncoder().encode(`${window.Shopify.shop}:${owner}`));
   return data.context===[...new Uint8Array(hash)].map(x=>x.toString(16).padStart(2,'0')).join('');
 }
 function analyticsStatus(value){analyticsState=value;document.dispatchEvent(new CustomEvent('hadaya:favorites-analytics',{detail:{status:value}}));}
 async function analyticsConsent(mode,g=generation){
   const mine=++analyticsRequest,cap=actionToken,guest=guestToken,epoch=analytics.epoch;
   analytics={...analytics,allowed:false};
   if(!analytics.active||!epoch||!cap){analyticsStatus('off');return;}
   analyticsStatus(mode==='withdraw'?'withdrawing':'connecting');
   // Dedicated keepalive request survives the core context abort. Never persist
   // a capability or retry queue after the visitor withdraws preferences.
   const nonce=crypto.randomUUID().replaceAll('-','');
   try{
     const response=await fetch(`${proxyBase}/api/favorites/tracking?favorites_route=tracking&favorites_nonce=${nonce}`,{method:'POST',credentials:'same-origin',headers:{'Content-Type':'application/json'},body:JSON.stringify({mode,epoch,requestId:crypto.randomUUID().replaceAll('-',''),analyticsAllowed:mode==='allow'&&analyticsAllowed(),guestToken:guest,actionToken:cap}),keepalive:true,cache:'no-store'});
     if(!response.ok)throw Error('analytics_unconfirmed');
     const data=await response.json();
     if(mine!==analyticsRequest)return;
     if(g!==generation){if(mode==='withdraw')analyticsStatus('off');return;}
     analytics={active:true,version:1,...data.tracking};
     analyticsStatus(mode==='withdraw'?'off':analyticsAllowed()?'ready':'off');
   }catch{
     if(mine===analyticsRequest)analyticsStatus(mode==='withdraw'?'withdrawal_unconfirmed':'unavailable');
   }
 }
 function analyticsContext(data,g){
   analyticsRequest++;analytics=data.tracking?.active?{...data.tracking}:{active:false};
   if(!analytics.active){analyticsStatus('off');return;}
   if(!analyticsAllowed()){void analyticsConsent('withdraw',g);return;}
   if(analytics.allowed){analyticsStatus('ready');return;}
   void analyticsConsent('allow',g);
 }
 function consentChanged(){
   // Stop capture synchronously; attempt erasure with the old owner-bound
   // capability before init clears cookies, records and controller state.
   const withdrawal=!analyticsAllowed()&&analytics.active;
   if(withdrawal)void analyticsConsent('withdraw');
   else if(!analyticsAllowed()){analytics={active:false};analyticsStatus('off');}
   return init();
 }
 function accept(data,g){assertCurrent(g);if(data.context!==context)throw new Error('context_changed');records=data.records;revision=data.revision;notify();}
 function tokenFrom(data){actionToken=data.actionToken;actionExpiresAt=Number(data.actionExpiresAt)||Date.now()/1000+600;}
 function invalidate(code='account_changed'){++generation;controller?.abort();records=[];actionToken='';analytics={active:false};analyticsRequest++;pending.clear();status=code;notify();}
 async function renew(g){
   const data=await request('context',{guestToken,preferencesAllowed:true,trackingVersion:1},g);
   if(!await expectedContext(data)||data.context!==context){invalidate();throw new Error('context_changed');}
   assertCurrent(g);tokenFrom(data);accept(data,g);analyticsContext(data,g);
 }
 async function authorized(route,body,g){
   if(Date.now()/1000>actionExpiresAt-30)await renew(g);
   // Stable operation/import identity survives token refresh and a lost response.
   const send=()=>{const payload={...body,guestToken,actionToken};if(payload.tracking&&!analyticsAllowed())payload.tracking={...payload.tracking,analyticsAllowed:false};return request(route,payload,g);};
   try{return await send();}
   catch(e){
     assertCurrent(g);
     if(e.httpStatus===403 && ['wrong_action_context','invalid_action'].includes(e.message)){await renew(g);if(route==='operation')body.expectedRevision=revision;return send();}
     if(!e.httpStatus && e.name!=='AbortError'){return send();}
     if(e.httpStatus===409 && e.message==='revision_conflict'){await renew(g);body.expectedRevision=revision;return send();}
     throw e;
   }
 }
 async function migrate(g){
   warnings=warnings.filter(x=>!x.startsWith('migration_'));
   try{
     const raw=storage('lovedPhrases'),binding=storage('hadaya_favorites_legacy_binding_v1');
     if(raw===null || (binding && binding!==context))return;
     // Reserve before sending. Raw legacy data is never rewritten or deleted.
     storage('hadaya_favorites_legacy_binding_v1',context);
     if(storage('hadaya_favorites_legacy_binding_v1')!==context)throw new Error('storage');
     const imported=await authorized('legacy',{raw},g);accept(imported,g);
     storage('hadaya_favorites_legacy_receipt_v1',JSON.stringify({context,receipt:imported.receipt}));
   }catch(e){assertCurrent(g);warn(e.httpStatus===413?'migration_capacity':e.message==='legacy_invalid_json'||e.message==='legacy_not_array'?'migration_format':'migration_retry');}
 }
 async function init(){
   const g=++generation;controller?.abort();controller=new AbortController();records=[];actionToken='';analytics={active:false};context='';warnings=[];pending.clear();status='loading';notify();
   try{
     if(!preferences()){clearGuest(cookie());account=false;status='session';notify();return;}
     guestToken=cookie();
     let startingGuest=guestToken;
     let lastAccount;
     try{lastAccount=storage('hadaya_favorites_last_account');}catch{warn('migration_storage');}
     let data;
     try{data=await request('context',{guestToken,preferencesAllowed:true,trackingVersion:1},g);}
     catch(e){if(e.httpStatus!==410&&e.message!=='invalid_guest_token')throw e;if(cookie()!==startingGuest){invalidate('unavailable');return;}clearGuest(startingGuest);startingGuest='';data=await request('context',{preferencesAllowed:true,trackingVersion:1},g);warn('guest_expired');}
     if(!await expectedContext(data)){invalidate();return;}
     assertCurrent(g);
     // Cookies are shared across tabs. A delayed issuer must not replace a
     // newer guest, and an old response must not delete another tab's token.
     if(cookie()!==startingGuest){invalidate('unavailable');return;}
     // A stale tab must not change shared cookies or account markers until the
     // signed proxy identity agrees with the identity rendered into that tab.
     let currentAccount;
     try{currentAccount=storage('hadaya_favorites_last_account');}catch{warn('migration_storage');}
     if(!config.customerId && currentAccount!==lastAccount){invalidate();return;}
     if(!config.customerId && currentAccount){
       // A plain shared marker does not prove logout or ownership of a cookie.
       // During an ambiguous transition, keep the guest capability for a later
       // verified account merge, but expose no old guest/account favorites.
       if(startingGuest){invalidate('unavailable');warn('session_transition');return;}
       try{storage('hadaya_favorites_last_account','');}catch{warn('migration_storage');}
     }
     if(config.customerId){try{storage('hadaya_favorites_last_account',String(config.customerId));}catch{warn('migration_storage');}}
     assertCurrent(g);context=data.context;account=data.account;tokenFrom(data);records=data.records;revision=data.revision;
     if(!account){
       guestToken=data.guestToken;
       document.cookie=`${cookieName}=${guestToken}; Max-Age=${Math.max(0,Math.floor((new Date(data.expiresAt).getTime()-Date.now())/1000))}; Path=/; Secure; SameSite=Lax`;
       if(cookie()!==guestToken)warn('cookie_unavailable');
     }
     status='ready';notify();analyticsContext(data,g);
     if(account && guestToken){
       try{const merged=await authorized('merge',{},g);accept(merged,g);clearGuest();}
       catch(e){assertCurrent(g);if(e.httpStatus===410){clearGuest();warn('guest_expired');}else warn('merge_retry');}
     }
     await migrate(g);assertCurrent(g);notify();
   }catch(e){if(g!==generation)return;records=[];status=e.message==='context_changed'?'account_changed':'unavailable';notify();}
 }
 function enqueue(type,reference,removed,snapshot,toggle=false){
   const g=generation,key=`${type}:${reference}`;
   // Ignore a repeated activation while that same heart is pending.
   if(pending.has(key))return Promise.resolve();
   pending.add(key);notify();
   const run=async()=>{
     assertCurrent(g);
     const remove=toggle?records.some(r=>r.key===key):removed;
     if(status==='session'){records=records.filter(r=>r.key!==key);if(!remove)records.push({type,reference:String(reference),key,snapshot});notify();return;}
     if(status!=='ready')throw new Error('favorites_unavailable');
     const operation={type,reference:String(reference),removed:remove,snapshot,operationId:crypto.randomUUID().replaceAll('-',''),expectedRevision:revision};
     if(type==='product'&&analytics.active&&analytics.allowed&&analyticsAllowed())operation.tracking={version:1,analyticsAllowed:true,epoch:analytics.epoch,creationTicket:actionToken};
     try{accept(await authorized('operation',operation,g),g);warnings=warnings.filter(x=>x!=='save_retry');}
     catch(e){assertCurrent(g);if(e.httpStatus===410 && !account){clearGuest();warn('guest_expired');}else warn('save_retry');throw e;}
   };
   const next=tail.then(run).finally(()=>{if(g===generation){pending.delete(key);notify();}});tail=next.catch(()=>{});return next;
 }
 window.HadayaFavorites={init,analyticsStatus:()=>analyticsState,set:(type,id,removed,snapshot)=>enqueue(type,String(id),removed,snapshot),toggle:(type,id,snapshot)=>enqueue(type,String(id),false,snapshot,true),get:()=>structuredClone(records),has:(type,id)=>records.some(r=>r.type===type&&r.reference===String(id)),isPending:(type,id)=>pending.has(`${type}:${id}`),status:()=>status,warnings:()=>[...warnings],retry:async()=>{const g=generation;if(status!=='ready'||(!account&&warnings.includes('guest_expired')))return init();if(account&&guestToken){try{accept(await authorized('merge',{},g),g);clearGuest();warnings=warnings.filter(x=>x!=='merge_retry');}catch(e){assertCurrent(g);if(e.httpStatus===410)clearGuest();warn('merge_retry');}}await migrate(g);notify();}};
 document.addEventListener('visitorConsentCollected',consentChanged);
 window.addEventListener('pageshow',init);
 window.addEventListener('pagehide',()=>invalidate('loading'));
 document.addEventListener('click',e=>{if(e.target.closest('a[href*="/account/logout"],a[href*="/customer_identity/logout"]')){clearGuest(cookie());invalidate('loading');}},true);
 document.addEventListener('visibilitychange',()=>{if(document.hidden&&account)invalidate('loading');else if(!document.hidden)init();});
 if(window.Shopify?.loadFeatures)window.Shopify.loadFeatures([{name:'consent-tracking-api',version:'0.1'}],()=>init());else init();
})();
